import * as v from "valibot";

// step1
export const IdentifySpecificGoalsSchema = v.pipe(
  v.object({
    goals: v.pipe(
      v.array(v.string()),
      v.minLength(1, "Please select at least one goal."),
      v.maxLength(2, "You cannot select more than 2 goals."),
    ),
    otherGoal: v.nullish(
      v.pipe(
        v.string(),
        v.trimStart(),
        v.nonEmpty("Please describe your goal in the text area."),
        v.maxLength(500, "Your description must be 500 characters or less."),
      ),
    ),
  }),
  v.transform((input) => ({
    ...input,
    otherGoal: input.otherGoal || undefined,
  })),
);

export type IdentifySpecificGoalsForm = v.InferInput<
  typeof IdentifySpecificGoalsSchema
>;

//step2
export const DefineUrgencyAndReadinessSchema = v.object({
  startTime: v.pipe(
    v.string(),
    v.nonEmpty(
      "Please select when you’re looking to start working towards your goals.",
    ),
  ),
  support: v.pipe(
    v.string(),
    v.nonEmpty("Please select how much support you need."),
  ),
});

export type DefineUrgencyAndReadinessForm = v.InferInput<
  typeof DefineUrgencyAndReadinessSchema
>;

// step 3
export const PreferredCoachingStyleAndFormatSchema = v.object({
  coachingFormats: v.pipe(
    v.string(),
    v.nonEmpty(
      "Please select the type of coaching experience you are looking for.",
    ),
  ),
  coachingApproaches: v.pipe(
    v.array(v.string()),
    v.minLength(1, "Please select at least one option."),
    v.maxLength(2, "You cannot select more than 2 options."),
  ),
});

export type PreferredCoachingStyleAndFormatForm = v.InferInput<
  typeof PreferredCoachingStyleAndFormatSchema
>;

// step 4
export const CoachExperienceLevelSchema = v.object({
  coachingPackages: v.pipe(
    v.array(v.string()),
    v.minLength(1, "Please select at least one experience level."),
  ),
});

export type CoachExperienceLevelForm = v.InferInput<
  typeof CoachExperienceLevelSchema
>;

// step 5
const CoachGenderPreferenceSchema = v.object({
  coachGender: v.pipe(
    v.string(),
    v.nonEmpty("Please select your gender preference."),
  ),
});

const CoachAgesPreferenceSchema = v.object({
  coachAges: v.pipe(
    v.array(v.string()),
    v.minLength(1, "Please select at least one age preference."),
  ),
});

export const CoachDemographicsPreferenceSchema = v.object({
  coachGender: v.pipe(
    v.string(),
    v.nonEmpty("Please select your gender preference."),
  ),
  coachAges: v.pipe(
    v.array(v.string()),
    v.minLength(1, "Please select at least one age preference."),
  ),
});

export type CoachDemographicsPreferenceForm = v.InferInput<
  typeof CoachDemographicsPreferenceSchema
>;

// step 6
export const UserAndCoachPersonalitySchema = v.object({
  userPersonality: v.pipe(
    v.array(v.string()),
    v.minLength(1, "Please select at least one option."),
    v.maxLength(2, "You cannot select more than 2 options."),
  ),
  coachPersonality: v.pipe(
    v.array(v.string()),
    v.minLength(1, "Please select at least one option."),
    v.maxLength(2, "You cannot select more than 2 options."),
  ),
});

export type UserAndCoachPersonalityForm = v.InferInput<
  typeof UserAndCoachPersonalitySchema
>;

// step 7
export const FormOfConductSchema = v.object({
  sessionDeliveryMethods: v.pipe(
    v.string(),
    v.nonEmpty("Please select your session format preference."),
  ),
  prefersCoachLocation: v.string(),
  coachLocationCountry: v.optional(
    v.pipe(
      v.string(),
      v.trimStart(),
      v.nonEmpty("Please provide a valid country."),
      v.maxLength(100, "Country name must be under 100 characters."),
    ),
  ),
  coachLocationRegion: v.optional(
    v.pipe(
      v.string(),
      v.trimStart(),
      v.maxLength(100, "City or region name must be under 100 characters."),
    ),
  ),
});

export type FormOfConductForm = v.InferInput<typeof FormOfConductSchema>;

// step 8
export const FaithBasedCoachingSchema = v.object({
  faithIntegration: v.pipe(
    v.array(
      v.union([
        v.literal("Christianity"),
        v.literal("Islam"),
        v.literal("Judaism"),
        v.literal("Hinduism"),
        v.literal("Buddhism"),
        v.literal("Spiritual but not Religious"),
        v.literal("Other"),
        v.literal("I do not integrate spirituality into my coaching"),
        v.literal("No preference"),
      ]),
    ),
    v.minLength(1, "Please select at least one preference."),
    v.custom((value: any) => {
      // Check if "No preference" is selected along with other options
      const hasNoPreference = value.includes("No preference");
      const hasOtherOptions = value.some((option: string) =>
        [
          "Christianity",
          "Islam",
          "Judaism",
          "Hinduism",
          "Buddhism",
          "Spiritual but not Religious",
          "I don't want a coach that integrates spirituality into their coaching",
          "Other",
        ].includes(option),
      );

      // Invalid if "No preference" and other options are selected together
      return !(hasNoPreference && hasOtherOptions);
    }, "You cannot select other preferences if 'No preference' is selected."),
  ),
  otherFaithIntegration: v.optional(
    v.pipe(
      v.string(),
      v.trimStart(),
      v.nonEmpty("Please describe your preference if 'Other' is selected."),
      v.maxLength(500, "Your description must be 500 characters or less."),
    ),
  ),
});

export type FaithBasedCoachingForm = v.InferInput<
  typeof FaithBasedCoachingSchema
>;

// step 9
export const PreferredLanguageSchema = v.object({
  preferredLanguages: v.pipe(
    v.array(v.string()),
    v.minLength(1, "Please select at least one preferred language."),
    v.maxLength(3, "You can select up to 3 languages only."),
  ),
});

export type PreferredLanguageForm = v.InferInput<
  typeof PreferredLanguageSchema
>;

// step 10
export const PersonalContext = v.object({
  personalContext: v.pipe(
    v.string(),
    v.trimStart(),
    v.nonEmpty("Please describe your personal context."),
    v.maxLength(500, "Your description must be 500 characters or less."),
  ),
});

export type PersonalContextForm = v.InferInput<typeof PersonalContext>;

export const CoachDiscoverySchema = v.object({
  ...IdentifySpecificGoalsSchema.entries,
  ...DefineUrgencyAndReadinessSchema.entries,
  ...PreferredCoachingStyleAndFormatSchema.entries,
  ...CoachExperienceLevelSchema.entries,
  ...CoachGenderPreferenceSchema.entries,
  ...FormOfConductSchema.entries,
  ...UserAndCoachPersonalitySchema.entries,
  ...CoachAgesPreferenceSchema.entries,
  ...FaithBasedCoachingSchema.entries,
  ...PreferredLanguageSchema.entries,
  ...PersonalContext.entries,
});

export type CoachDiscoveryForm = v.InferInput<typeof CoachDiscoverySchema>;
export type GoalOptions = v.InferOutput<
  typeof CoachDiscoverySchema
>["goals"][number];
export type CoachingStyleOptions = v.InferOutput<
  typeof CoachDiscoverySchema
>["coachingApproaches"][number];
export type CoachExperienceOptions = v.InferOutput<
  typeof CoachDiscoverySchema
>["coachingPackages"][number];
export type CoachAgesOptions = v.InferOutput<
  typeof CoachDiscoverySchema
>["coachAges"][number];
export type FaithIntegrationOptions = v.InferOutput<
  typeof CoachDiscoverySchema
>["faithIntegration"][number];

export type CoachDiscoveryStep = {
  title: string;
  subTitle?: string;
  fields: string[];
};
/*[
    "careerBusinessSpecialties", 
    "healthFitnessSpecialties",
    "personalDevelopmentSpecialties", 
    "relationshipSpecialties", 
    "stressWellbeingSpecialties"
    "financialSpecialties"
    "otherSpecialties"
  ] */

export const goalsField = {
  name: "goals",
  options: [
    {
      label: "Advance my career/business",
      value: "Career & Business Coaching",
      description:
        "Business & Leadership Coaching, Mentorship, Executive Coaching",
    },
    {
      label: "Improve my health & fitness",
      value: "Health & Fitness Coaching",
      description: "Health Coaching, Wellness, Energy Work",
    },
    {
      label: "Enhance personal development",
      value: "Personal Development Coaching",
      description: "Life Coaching, Mindset Coaching, Spiritual Guidance",
    },
    {
      label: "Build stronger relationships",
      value: "Relationship, Dating & Family Coaching",
      description: "Relationship Coaching, Communication Skills Coaching",
    },
    {
      label: "Manage stress and well-being",
      value: "Stress, Emotional Resilience & Well-being Coaching",
      description: "Mindfulness Coaching, Therapy, Stress Management",
    },
    {
      label: "Improve financial management",
      value: "Financial & Wealth Coaching",
      description: "Financial Coaching, Wealth Strategy Coaching",
    },
    {
      label: "Enhance sports performance",
      value: "Sports & Performance Coaching",
      description: "Sports Coaching, Performance Training, Self-Defense",
    },
    { value: "Other", label: "Other" },
  ],
};

export const otherGoalField = {
  name: "otherGoal",
  placeholder: "Enter your goals here..",
  maxLength: 500,
};
export const specificGoalsStep: CoachDiscoveryStep = {
  title: "What goals are you looking to achieve with a coach?",
  fields: ["goals", "otherGoal"],
};

export const startTimeField = {
  name: "startTime",
  subTitle: "When would you like to start working with a coach?",
  options: [
    { value: "Immediately", label: "Immediately" },
    { value: "In the next month", label: "In the next month" },
    { value: "In a few months", label: "In a few months" },
    { value: "I'm flexible", label: " I'm flexible" },
  ],
};
export const supportField = {
  name: "support",
  subTitle: "How much support do you need?",
  options: [
    {
      value: "Tactical coaching, consulting, strategy sessions",
      label: "A quick solution for a specific challenge",
      description: "Tactical coaching, consulting, strategy sessions",
    },
    {
      value: "Mentorship, business & career coaching, structured programs",
      label: "Ongoing skill-building & growth",
      description:
        "Mentorship, business & career coaching, structured programs",
    },
    {
      value: "Life coaching, therapy, energy healing, mindset shifts",
      label: "Deep, long-term transformation",
      description: "Life coaching, therapy, energy healing, mindset shifts",
    },
    {
      value: "Not sure yet – I'd like guidance on this",
      label: "Not sure yet – I'd like guidance on this",
    },
  ],
};

export const urgencyAndReadinessStep: CoachDiscoveryStep = {
  title:
    "How soon do you want to start, and what level of support do you need?",
  fields: ["startTime", "support"],
};

//coachingFormats
//coachingApproaches

export const coachingFormatsFields = {
  name: "coachingFormats",
  subTitle: "Your Preferred Format",
  options: [
    { value: "1-on-1", label: "1:1 Coaching only" },
    { value: "both", label: "Open to group coaching or 1:1" },
    { value: "group", label: "Prefer group coaching" },
    { value: "undecided", label: "Not sure yet" },
    { value: "any", label: "No preference" },
  ],
};

export const coachingApproachesField = {
  name: "coachingApproaches",
  subTitle: "How do you prefer to be guided?",
  options: [
    {
      value: "Exploratory & Self-Reflective",
      label: "Exploratory & Self-Reflective",
      description:
        "Transformational Coaching, Therapy, Healing – Helps uncover answers through deep questioning",
    },
    {
      value: "Goal-oriented & action-driven",
      label: "Goal-oriented & action-driven",
      description:
        "Consulting, Business Coaching, Executive Coaching – structured, clear strategies with defined outcomes",
    },
    {
      value: "Supportive & Encouraging",
      label: "Supportive & Encouraging",
      description:
        "Wellness Coaching, Relationship Coaching – Motivational, compassionate, and accountability-driven",
    },
    { value: "Not sure yet", label: "Not sure yet" },
  ],
};

export const coachingFormatsAndApproachesStep: CoachDiscoveryStep = {
  title: "What type of coaching experience are you looking for?",
  fields: ["coachingFormats", "coachingApproaches"],
};
//coachingPackages
export const coachingPackagesField = {
  name: "coachingPackages",
  subTitle:
    "All Dream Coach Match coaches offer results-based packages, not hourly rates. You're investing in outcomes, not time.",

  options: [
    {
      value: "Rising Coach",
      label: "Rising Coach: Goal-Driven Starter Package",
      description: "Get clear and build momentum with an early-stage coach.",
    },
    {
      value: "Experienced Coach",
      label: "Experienced Coach: Proven Results Program",
      description: "Work with a seasoned professional to achieve your goals.",
    },
    {
      value: "Master Coach",
      label: "Master Coach: Elite Transformation Partnership",
      description:
        "Partner with an industry-leading expert for life-changing results.",
    },
    {
      value: "No preference",
      label: "No preference – I just want the best coach for me",
    },
  ],
};
export const coachPackagesStep: CoachDiscoveryStep = {
  title: "Which coaching package best aligns with your needs?",
  fields: ["coachingPackages"],
};

export const coachGenderField = {
  name: "coachGender",
  subTitle: "Do you have a gender preference?",
  options: [
    { value: "Woman", label: "Woman coach" },
    { value: "Man", label: "Man coach" },
    { value: "Non-Binary", label: "Non-Binary coach" },
    { value: "No preference", label: "No preference" },
  ],
};

export const coachAgesField = {
  name: "coachAges",
  subTitle: "Do you have an age range preference for your coach?",
  options: [
    { value: "20-30", label: "20-30" },
    { value: "30-40", label: "30-40" },
    { value: "40-50", label: "40-50" },
    { value: "50+", label: "50+" },
    { value: "No preference", label: "No preference" },
  ],
};

export const coachDemographicsPreferenceStep: CoachDiscoveryStep = {
  title: "Coach Demographics Preferences",
  fields: ["coachGender", "coachAges"],
};

export const userPersonalityField = {
  name: "userPersonality",
  subTitle: "Which of these best describes your personality?",
  options: [
    {
      value: "Logical & Strategic Thinker",
      label: "Logical & Strategic Thinker",
      description:
        'Analytical, structured, makes data-driven decisions – Thinkers, Planners, INTJ, Type 5, DISC "C" Profile, Projector',
    },
    {
      value: "Creative & Visionary",
      label: "Creative & Visionary",
      description:
        'Big-picture thinker, enjoys innovation, thrives on ideas – Free-spirited, ENFP, Type 4, DISC "I" Profile, Manifesting Generator',
    },
    {
      value: "Empathetic & Supportive",
      label: "Empathetic & Supportive",
      description:
        'Deeply values relationships, harmony, and emotional connection – INFJ, Type 2, DISC "S" Profile, Reflector',
    },
    {
      value: "Practical & Action-Oriented",
      label: "Practical & Action-Oriented",
      description:
        'Hands-on, efficient, loves getting things done – ESTJ, Type 3, DISC "D" Profile, Manifestor',
    },
    {
      value: "Intuitive & Reflective",
      label: "Intuitive & Reflective",
      description:
        'Inner-focused, values deep meaning, self-awareness, and mindfulness – INFP, Type 9, DISC "S" Profile, Generator',
    },
    {
      value: "Energetic & Fast-Paced",
      label: "Energetic & Fast-Paced",
      description:
        'Highly driven, adaptable, thrives in dynamic environments – ENTP, Type 7, DISC "I" Profile, Manifesting Generator',
    },
    {
      value: "Skip this question",
      label: "Skip this question",
    },
  ],
};

export const coachPersonalityField = {
  name: "coachPersonality",
  subTitle: "What kind of coach personality would you connect best with?",
  options: [
    {
      value: "Logical & Strategic Thinker",
      label: "Logical & Strategic Thinker",
      description:
        "Helps create structured systems & frameworks for success – Thinkers, Strategists, Planners.",
    },
    {
      value: "Energetic & Motivational",
      label: "Energetic & Motivational",
      description:
        "High-energy, dynamic, and keeps you accountable – Visionaries, Leaders, Achievers.",
    },
    {
      value: "Compassionate & Deep Listener",
      label: "Compassionate & Deep Listener",
      description:
        "Creates a safe space, helps you process emotions – Counselors, Healers, Nurturers",
    },
    {
      value: "Spiritual & Holistic",
      label: "Spiritual & Holistic",
      description:
        "Integrates mindfulness, energy work, or alternative approaches – Intuitive, Energy Practitioners, Guides",
    },
    {
      value: "Action-Oriented & Results-Driven",
      label: "Action-Oriented & Results-Driven",
      description:
        "Takes a no-nonsense approach, Focuses on measurable outcomes – Entrepreneurs, Coaches, Business Mentors",
    },
    {
      value: "Skip this question",
      label: "Skip this question",
    },
  ],
};

export const personalityStep: CoachDiscoveryStep = {
  title: "Do you want to be matched based on personality compatibility?",
  subTitle:
    "We use a blend of personality insights from DISC, Myers-Briggs (MBTI), Enneagram, and Human Design to match you with a coach who aligns with your natural style. You can skip this step if you prefer.",
  fields: ["userPersonality", "coachPersonality"],
};

export const sessionDeliveryMethodsFeild = {
  name: "sessionDeliveryMethods",
  subTitle: "How would you like your coaching sessions to be conducted?",
  options: [
    { value: "online", label: "Online" },
    { value: "in-person", label: "In-person" },
    {
      value: "hybrid",
      label: "Hybrid (a mix of online and in-person)",
    },
    { value: "No preference", label: "No preference" },
  ],
};

export const prefersCoachLocationField = {
  name: "prefersCoachLocation",
  subTitle: "Do you have a preference for where your coach is based?",
  options: [
    { value: "Yes", label: "Yes" },
    { value: "No preference", label: "No preference" },
  ],
};

export const coachLocationCountryField = {
  name: "coachLocationCountry",
  subTitle: "Where would you like your coach to be from?",
  placeholder: "Country",
};

export const coachLocationRegionField = {
  name: "coachLocationRegion",
  subTitle: "Type a region or city if you have a preference:",
  placeholder: "City or region",
};

export const formOfConductStep: CoachDiscoveryStep = {
  title: "Coaching Session Format",
  fields: [
    "sessionDeliveryMethods",
    "prefersCoachLocation",
    "coachLocationCountry",
    "coachLocationRegion",
  ],
};

export const faithIntegrationsField = {
  name: "faithIntegration",
  options: [
    { value: "Christianity", label: "Christianity" },
    { value: "Islam", label: "Islam" },
    { value: "Judaism", label: "Judaism" },
    { value: "Hinduism", label: "Hinduism" },
    { value: "Buddhism", label: "Buddhism" },
    {
      value: "Spiritual but not Religious",
      label: "Spiritual but not Religious",
    },
    { value: "Other", label: "Other" },
    {
      value: "I do not integrate spirituality into my coaching",
      label:
        "I don't want a coach that integrates spirituality into their coaching",
    },
    { value: "No preference", label: "No preference" },
  ],
};

export const otherFaithIntegrationField = {
  name: "otherFaithIntegration",
  placeholder: "Share your beliefs and values here...",
  maxLength: 500,
};

export const faithBasedCoachingStep: CoachDiscoveryStep = {
  title:
    "Would you like a coach who integrates faith or spirituality into their coaching?",
  fields: ["faithIntegration", "otherFaithIntegration"],
};

export const preferredLanguagesStep: CoachDiscoveryStep = {
  title:
    "Please select up to three preferred languages for coaching from the dropdown list.",
  fields: ["preferredLanguages"],
};

export const personalContextField = {
  name: "personalContext",
  placeholder: "Describe your personal context here...",
  maxLength: 500,
};

export const personalContextStep: CoachDiscoveryStep = {
  title:
    "What specific challenges are you facing or goals you want to achieve through coaching? Sharing this will help us find the best coach for you.",
  fields: ["personalContext"],
};

// Order is important for the validation function to parse the fields for validation on each step
export const coachDiscoverySteps: Array<CoachDiscoveryStep> = [
  specificGoalsStep,
  urgencyAndReadinessStep,
  coachingFormatsAndApproachesStep,
  coachPackagesStep,
  coachDemographicsPreferenceStep,
  personalityStep,
  formOfConductStep,
  faithBasedCoachingStep,
  preferredLanguagesStep,
  personalContextStep,
];
